import React from "react";
import memoize from "memoize-weak";
import bind from "memoize-bind";
import { uniq, uniqBy, sortBy, filter, debounce } from "lodash";

import {
  Form,
  FormControl,
  OrderDecorator,
  FilterGroup,
  AppSettings,
  st,
  UsersRepository,
  ProductsRepository,
} from "lib/index";

const EMPTY_OTHER_FILTERS = {
  marketplace: null,
  saas: null,
  manual: null,
  automatic: null,
  requires_company_approval: null,
  doesnt_require_company_approval: null,
  no_booking: null,
  static_date_booking: null,
  static_date_range_booking: null,
  without_agent_commision_and_service_fee: null,
  with_agent_commision: null,
  with_agent_service_fee: null,
  with_both_agent_commision_and_service_fee: null,
  with_saas_commision: null,
};

export default class OrdersListFilters extends React.PureComponent {
  state = {
    search: "",
    loading: false,
  };

  componentDidMount() {
    this.getUsers();
  }

  componentWillUnmount() {
    this.subscriptions.forEach((s) => s.unsubscribe());
    this.subscriptions.length = 0;
  }

  handleFilterChange(prop, value) {
    this.handleFiltersChange({ [prop]: value });
  }

  handleFiltersChange(data) {
    const { filters, onFiltersChange } = this.props;
    onFiltersChange({
      ...filters,
      ...data,
    });
  }

  filterCurrentProducts(currentProducts, filterId) {
    const subscribedAndCurrentProducts = uniqBy([...this.userProducts, ...currentProducts], (p) => p.id);

    if (!subscribedAndCurrentProducts) {
      return [];
    }

    if (!filterId) {
      return subscribedAndCurrentProducts;
    }

    return filter(subscribedAndCurrentProducts, (product) => product.company.id == filterId);
  }

  // filterAgents(agents, companyId) {
  //   if (!agents) {
  //     return [];
  //   }
  //   if (!companyId) {
  //     return agents;
  //   }
  //   return filter(agents, agent => agent.all_agent_cooperations && agent.all_agent_cooperations.some(obj => obj.supplier.id == companyId));
  // }

  // filterSuppliers(suppliers, agentId) {
  //   if (!suppliers) {
  //     return [];
  //   }
  //   if (!agentId) {
  //     return suppliers;
  //   }
  //   return filter(suppliers, supplier => supplier.all_agent_cooperations && supplier.all_agent_cooperations.some(obj => obj.agent.id == agentId));
  // }
  subscriptions = [];
  receptionists = [];
  userProducts = [];

  getProducts(params) {
    this.setState({ loading: true });
    const sub = ProductsRepository.search(params).subscribe((products) => {
      products.records.map((product) => {
        this.userProducts.push(product);
      });
      this.setState({ loading: false });
    });
    this.subscriptions.push(sub);
  }

  getUsers() {
    //get users
    const sub = UsersRepository.getAll().subscribe((users) => {
      let isCurrentUserPresent = false;
      let filteredUsers = [];

      users.map((u) => {
        filteredUsers.push([u.id, u.admin_user.full_name]);

        // check if currently logged in user in present on the list
        if (u.id === st.authorize.member.id) {
          isCurrentUserPresent = true;
        }
      });

      // if its not present - add it.
      if (!isCurrentUserPresent) {
        filteredUsers.push([st.authorize.member.id, st.authorize.user.full_name]);
      }

      this.receptionists = [...filteredUsers];
    });
    this.subscriptions.push(sub);
  }

  getReceptionistOptions(aggregations) {
    const receptionistOptions = [];

    if (aggregations && aggregations.assigned_creator_id) {
      this.addNoneReceptionist(aggregations, receptionistOptions);
      this.receptionists.forEach((receptionist) => {
        const id = receptionist[0];
        const name = receptionist[1];

        receptionistOptions.push([id, `${name} (${aggregations.assigned_creator_id[id] || 0})`]);
      });
    }

    return receptionistOptions;
  }

  addNoneReceptionist(aggregations, receptionistOptions) {
    if (aggregations.assigned_creator_id[-1] !== undefined) {
      receptionistOptions.push([-1, `${I18n.t("papp.common.none")} (${aggregations.assigned_creator_id[-1]})`]);
    }
  }

  handleGetNewProducts = debounce((value) => {
    if (value.length >= 2) {
      this.getProducts({ search: value });
    }
  }, 350);

  render() {
    const {
      currentCompany,
      currentProducts,
      filters,
      aggregations,
      showStFilters = st.authorize.company.is_st || st.authorize.member.role === "global_admin",
    } = this.props;

    const filterStatusOptions = OrderDecorator.statuses.map((v) => [
      v,
      `${OrderDecorator.getStatusLabel(v, true)} (${(aggregations && aggregations.status[v]) || 0})`,
    ]);

    const filterPricingPlanTypeOptions = OrderDecorator.pricingPlanTypes
      .filter((v) => aggregations && aggregations.pricing_plan_type[v] > 0)
      .map((v) => [
        v,
        `${OrderDecorator.getPricingPlanTypeLabel(v)} (${(aggregations && aggregations.pricing_plan_type[v]) || 0})`,
      ]);

    const filterProductTypeOptions = OrderDecorator.productTypes
      .filter((v) => aggregations && aggregations.product_type[v] > 0)
      .map((v) => [
        v,
        `${OrderDecorator.getProductTypeLabel(v)} (${(aggregations && aggregations.product_type[v]) || 0})`,
      ]);

    const supplierOptions = sortBy(
      uniqBy(
        [
          ...currentCompany.getPossibleSuppliers(),
          ...(st.authorize.member.role === "global_admin"
            ? st.authorize.user.accessed_companies.filter((c) => c.supplier)
            : []),
        ],
        (c) => c.id
      ),
      (c) => c.name
    ).map((c) => [c.id, `${c.name} (${(aggregations && aggregations.company_id[c.id]) || 0})`]);

    const agentOptions = [
      [-1, `${I18n.t("papp.common.none")} (${(aggregations && aggregations.agent_id[-1]) || 0})`],
      ...sortBy(
        uniqBy(
          [
            ...currentCompany.getPossibleAgents(),
            ...(st.authorize.member.role === "global_admin"
              ? st.authorize.user.accessed_companies.filter((c) => c.agent)
              : []),
          ],
          (c) => c.id
        ),
        (c) => c.name
      ).map((c) => [c.id, `${c.name} (${(aggregations && aggregations.agent_id[c.id]) || 0})`]),
    ];

    const affiliateOptions = [
      [-1, `${I18n.t("papp.common.none")} (${(aggregations && aggregations.affiliate_id[-1]) || 0})`],
      ...sortBy(
        uniqBy(
          [
            ...(st.authorize.member.role === "global_admin"
              ? st.authorize.user.accessed_companies.filter((c) => c.affiliate)
              : []),
          ],
          (c) => c.id
        ),
        (c) => c.name
      ).map((c) => [c.id, `${c.name} (${(aggregations && aggregations.affiliate_id[c.id]) || 0})`]),
    ];

    const authorPlaceOptions = [
      [
        "frontend",
        `${I18n.humanAttributeName("order", "author_place_frontend")} (${
          (aggregations && aggregations.author_place["frontend"]) || 0
        })`,
      ],
      [
        "partnerapp",
        `${I18n.humanAttributeName("order", "author_place_partnerapp")} (${
          (aggregations && aggregations.author_place["partnerapp"]) || 0
        })`,
      ],
      [
        "customerapp",
        `${I18n.humanAttributeName("order", "author_place_customerapp")} (${
          (aggregations && aggregations.author_place["customerapp"]) || 0
        })`,
      ],
    ];

    const paymentMethodOptions = [
      "none",
      "payu",
      "dotpay",
      "paypal",
      // "payu",
      "custom_online_payment",
      "stripe",
      "agent_bank_wire",
      "supplier_bank_wire",
      "agent_cash",
      "supplier_cash",
      "terminal",
    ].map((v) => [
      v,
      `${v === "none" ? I18n.t("papp.common.none") : I18n.humanAttributeName("order", v)} (${
        (aggregations && aggregations.customer_payment_methods[v]) || 0
      })`,
    ]);

    const agentDisbursementStatusOptions = ["uncleared", "cleared"].map((v) => [
      v,
      `${I18n.humanAttributeName("order", v)} (${(aggregations && aggregations.agent_disbursement_status[v]) || 0})`,
    ]);

    // The aggregation itself is guarded, not just the whole set: the panel and the backend
    // deploy separately, and reading it off a response that predates the filter would take
    // the whole Finances page down.
    const platformDisbursementStatusOptions = ["uncleared", "cleared"].map((v) => [
      v,
      `${I18n.humanAttributeName("order", v)} (${((aggregations && aggregations.platform_disbursement_status) || {})[v] || 0})`,
    ]);

    const receptionistOptions = this.getReceptionistOptions(aggregations);

    return (
      <Form>
        <FilterGroup enabled>
          <div className="u-positionRelative">
            <FormControl.Input
              placeholder={I18n.humanAttributeName("order", "id")}
              value={filters.id}
              onChange={bind(this.handleFilterChange, this, "id")}
              fluid
              inputClassName="u-paddingRight"
            />
            <FormControl.DeleteRowButton
              hidden={filters.id === null}
              onClick={bind(this.handleFilterChange, this, "id", null)}
            />
          </div>

          <div className="u-positionRelative u-paddingRight">
            <FormControl.ProductsSelect
              products={this.filterCurrentProducts(currentProducts, filters.company_id)}
              placeholder={I18n.humanAttributeName("order", "product")}
              value={filters.product_id}
              onChange={bind(this.handleFilterChange, this, "product_id")}
              fluid
              className="u-dropdownMenuWhiteSpaceNormal"
              onInput={(e) => {
                this.setState({ search: e.target.value });
                this.handleGetNewProducts(e.target.value);
              }}
            />
            <FormControl.DeleteRowButton
              hidden={filters.product_id === null}
              onClick={bind(this.handleFilterChange, this, "product_id", null)}
            />
          </div>

          <div className="u-positionRelative">
            <FormControl.Input
              placeholder={I18n.t("papp.orders.OrdersListView.customer_query_filter")}
              value={filters.customer_query}
              onChange={bind(this.handleFilterChange, this, "customer_query")}
              fluid
              inputClassName="u-paddingRight"
            />
            <FormControl.DeleteRowButton
              hidden={filters.customer_query === null}
              onClick={bind(this.handleFilterChange, this, "customer_query", null)}
            />
          </div>
        </FilterGroup>

        <FilterGroup
          label={I18n.humanAttributeName("order", "product_type")}
          enabled={filters.product_type !== null}
          onClear={bind(this.handleFilterChange, this, "product_type", null)}
        >
          <FormControl.Select
            fluid
            search
            includeBlank
            value={filters.product_type}
            onChange={bind(this.handleFilterChange, this, "product_type")}
            options={filterProductTypeOptions}
          />
        </FilterGroup>

        <FilterGroup
          label={I18n.humanAttributeName("order", "status")}
          enabled={filters.status !== null}
          onClear={bind(this.handleFilterChange, this, "status", null)}
        >
          <FormControl.MultipleCheckbox
            value={filters.status}
            onChange={bind(this.handleFilterChange, this, "status")}
            options={filterStatusOptions}
            grouped
          />
        </FilterGroup>

        <FilterGroup
          label={I18n.humanAttributeName("order", "payment_type")}
          enabled={filters.payment_type !== null}
          onClear={bind(this.handleFilterChange, this, "payment_type", null)}
        >
          <FormControl.Select
            fluid
            includeBlank
            value={filters.payment_type}
            onChange={bind(this.handleFilterChange, this, "payment_type")}
            options={[
              [
                "online",
                `${I18n.humanAttributeName("order", "payment_type_online")} (${
                  (aggregations && aggregations.payment_type.online) || 0
                })`,
              ],
              [
                "offline",
                `${I18n.humanAttributeName("order", "payment_type_offline")} (${
                  (aggregations && aggregations.payment_type.offline) || 0
                })`,
              ],
            ]}
          />
        </FilterGroup>

        <FilterGroup
          label={I18n.humanAttributeName("order", "balance_status")}
          enabled={filters.balance_status !== null}
          onClear={bind(this.handleFilterChange, this, "balance_status", null)}
        >
          <FormControl.MultipleCheckbox
            value={filters.balance_status}
            onChange={bind(this.handleFilterChange, this, "balance_status")}
            options={OrderDecorator.balance_status_options.map(([v, label]) => [
              v,
              `${label} (${(aggregations && aggregations.balance_status[v]) || 0})`,
            ])}
            grouped
          />
        </FilterGroup>

        <FilterGroup
          label={I18n.humanAttributeName("order", "created_on")}
          enabled={filters.created_on !== null}
          onClear={bind(this.handleFilterChange, this, "created_on", null)}
        >
          <FormControl.SeparateDateRange
            value={filters.created_on ? [filters.created_on.gte || null, filters.created_on.lte || null] : null}
            onChange={([gte, lte]) => {
              this.handleFilterChange("created_on", { gte, lte });
            }}
          />
        </FilterGroup>

        <FilterGroup
          label={I18n.humanAttributeName("order", "stock_time_range")}
          enabled={filters.stock_start_date !== null || filters.stock_end_date !== null}
          onClear={bind(this.handleFiltersChange, this, {
            stock_start_date: null,
            stock_end_date: null,
          })}
        >
          <span>{I18n.humanAttributeName("order", "stock_start_date")}</span>
          <FormControl.SeparateDateRange
            value={
              filters.stock_start_date
                ? [filters.stock_start_date.gte || null, filters.stock_start_date.lte || null]
                : null
            }
            onChange={([gte, lte]) => {
              this.handleFilterChange("stock_start_date", { gte, lte });
            }}
          />
          <span>{I18n.humanAttributeName("order", "stock_end_date")}</span>
          <FormControl.SeparateDateRange
            value={
              filters.stock_end_date ? [filters.stock_end_date.gte || null, filters.stock_end_date.lte || null] : null
            }
            onChange={([gte, lte]) => {
              this.handleFilterChange("stock_end_date", { gte, lte });
            }}
          />
        </FilterGroup>

        <FilterGroup
          label={I18n.humanAttributeName("order", "receptionist")}
          enabled={filters.assigned_creator_id !== null}
          onClear={bind(this.handleFilterChange, this, "assigned_creator_id", null)}
        >
          <FormControl.Select
            fluid
            search
            includeBlank
            value={filters.assigned_creator_id}
            onChange={bind(this.handleFilterChange, this, "assigned_creator_id")}
            options={receptionistOptions}
          />
        </FilterGroup>

        {currentCompany.agent && (
          <FilterGroup
            label={I18n.humanAttributeName("order", "supplier")}
            enabled={filters.company_id !== null}
            onClear={bind(this.handleFilterChange, this, "company_id", null)}
          >
            <FormControl.Select
              fluid
              search
              includeBlank
              value={filters.company_id}
              onChange={bind(this.handleFilterChange, this, "company_id")}
              options={supplierOptions}
            />
          </FilterGroup>
        )}

        {(currentCompany.supplier || st.authorize.member.role === "global_admin") && (
          <FilterGroup
            label={I18n.humanAttributeName("order", "agent")}
            enabled={filters.agent_id !== null}
            onClear={bind(this.handleFilterChange, this, "agent_id", null)}
          >
            <FormControl.Select
              fluid
              search
              includeBlank
              value={filters.agent_id}
              onChange={bind(this.handleFilterChange, this, "agent_id")}
              options={agentOptions}
            />
          </FilterGroup>
        )}

        {showStFilters && (
          <FilterGroup
            label={I18n.humanAttributeName("order", "affiliate")}
            enabled={filters.affiliate_id !== null}
            onClear={bind(this.handleFilterChange, this, "affiliate_id", null)}
          >
            <FormControl.CheckboxFormGroup
              label={`${I18n.t("papp.orders.OrdersListView.with_affiliate_filter")} (${
                (aggregations && aggregations.with_affiliate[1]) || 0
              })`}
              checked={filters.with_affiliate === true}
              indeterminate={filters.with_affiliate === null}
              onChange={bind(this.handleFilterChange, this, "with_affiliate")}
            />
            <FormControl.Select
              fluid
              search
              includeBlank
              value={filters.affiliate_id}
              onChange={bind(this.handleFilterChange, this, "affiliate_id")}
              options={affiliateOptions}
            />
          </FilterGroup>
        )}

        <FilterGroup
          label={I18n.humanAttributeName("order", "author_place")}
          enabled={filters.author_place !== null}
          onClear={bind(this.handleFilterChange, this, "author_place", null)}
        >
          <FormControl.MultipleCheckbox
            value={filters.author_place}
            onChange={bind(this.handleFilterChange, this, "author_place")}
            options={authorPlaceOptions}
            grouped
          />
        </FilterGroup>

        <FilterGroup
          label={I18n.humanAttributeName("order", "customer_locale")}
          enabled={filters.customer_locale !== null}
          onClear={bind(this.handleFilterChange, this, "customer_locale", null)}
        >
          <FormControl.LocaleSelect
            value={filters.customer_locale}
            onChange={bind(this.handleFilterChange, this, "customer_locale")}
            includeBlank
            locales="customer_locales"
          />
        </FilterGroup>

        {showStFilters && (
          <FilterGroup
            label={I18n.humanAttributeName("order", "agent_disbursement")}
            enabled={filters.agent_disbursement_id !== null || filters.agent_disbursement_status !== null}
            onClear={bind(this.handleFiltersChange, this, {
              agent_disbursement_id: null,
              agent_disbursement_status: null,
            })}
          >
            <FormControl.InputFormGroup
              type="tel"
              placeholder={I18n.humanAttributeName("agent_disbursement", "id")}
              value={filters.agent_disbursement_id}
              onChange={bind(this.handleFilterChange, this, "agent_disbursement_id")}
              fluid
            />

            <FormControl.MultipleCheckboxFormGroup
              label={I18n.humanAttributeName("agent_disbursement", "status")}
              value={filters.agent_disbursement_status}
              onChange={bind(this.handleFilterChange, this, "agent_disbursement_status")}
              options={agentDisbursementStatusOptions}
              grouped
            />
          </FilterGroup>
        )}

        {showStFilters && (
          <FilterGroup
            label={I18n.humanAttributeName("order", "platform_disbursement_status")}
            enabled={filters.platform_disbursement_status !== null}
            onClear={bind(this.handleFilterChange, this, "platform_disbursement_status", null)}
          >
            <FormControl.MultipleCheckbox
              value={filters.platform_disbursement_status}
              onChange={bind(this.handleFilterChange, this, "platform_disbursement_status")}
              options={platformDisbursementStatusOptions}
              grouped
            />
          </FilterGroup>
        )}

        <FilterGroup
          label={I18n.humanAttributeName("order", "pricing_plan_type")}
          enabled={filters.pricing_plan_type !== null}
          onClear={bind(this.handleFilterChange, this, "pricing_plan_type", null)}
        >
          <FormControl.Select
            fluid
            includeBlank
            value={filters.pricing_plan_type}
            onChange={bind(this.handleFilterChange, this, "pricing_plan_type")}
            options={filterPricingPlanTypeOptions}
          />
        </FilterGroup>

        <FilterGroup
          label={I18n.t("papp.orders.OrdersListView.customer_payment_methods_filter")}
          enabled={filters.customer_payment_methods !== null}
          onClear={bind(this.handleFilterChange, this, "customer_payment_methods", null)}
        >
          <FormControl.MultipleCheckbox
            value={filters.customer_payment_methods}
            onChange={bind(this.handleFilterChange, this, "customer_payment_methods")}
            options={paymentMethodOptions}
            grouped
          />
        </FilterGroup>

        {showStFilters && (
          <FilterGroup
            label={I18n.t("papp.orders.OrdersListView.customer_transfer_date_filter")}
            enabled={filters.customer_transfer_date !== null}
            onClear={bind(this.handleFilterChange, this, "customer_transfer_date", null)}
          >
            <FormControl.SeparateDateRange
              value={
                filters.customer_transfer_date
                  ? [filters.customer_transfer_date.gte || null, filters.customer_transfer_date.lte || null]
                  : null
              }
              onChange={([gte, lte]) => {
                this.handleFilterChange("customer_transfer_date", { gte, lte });
              }}
            />
          </FilterGroup>
        )}

        {showStFilters && (
          <FilterGroup
            label={I18n.t("papp.orders.OrdersListView.total_amount_currencies_filter")}
            enabled={filters.total_amount_currencies !== null}
            onClear={bind(this.handleFilterChange, this, "total_amount_currencies", null)}
          >
            <FormControl.MultipleCheckbox
              value={filters.total_amount_currencies}
              onChange={bind(this.handleFilterChange, this, "total_amount_currencies")}
              options={AppSettings.backend_currencies
                .filter((currency) => !["GBP", "TRY"].includes(currency))
                .map((v) => [v, `${v} (${(aggregations && aggregations.total_amount_currencies[v]) || 0})`])}
              grouped
            />
          </FilterGroup>
        )}

        {showStFilters && (
          <FilterGroup
            label={I18n.humanAttributeName("order", "payment_profile")}
            enabled={filters.payment_profile_id !== null && filters.payment_profile_name !== null}
            onClear={bind(this.handleFiltersChange, this, {
              payment_profile_id: null,
              payment_profile_name: null,
            })}
          >
            <FormControl.Input
              type="tel"
              value={filters.payment_profile_id}
              onChange={bind(this.handleFilterChange, this, "payment_profile_id")}
              placeholder={I18n.humanAttributeName("payment_profile", "id")}
              fluid
            />

            <FormControl.Input
              value={filters.payment_profile_name}
              onChange={bind(this.handleFilterChange, this, "payment_profile_name")}
              placeholder={I18n.humanAttributeName("payment_profile", "name")}
              fluid
            />
          </FilterGroup>
        )}

        {showStFilters && (
          <FilterGroup
            label={I18n.t("papp.orders.OrdersListView.notes_filter")}
            enabled={filters.notes !== null || filters.with_notes !== null}
            onClear={bind(this.handleFiltersChange, this, {
              notes: null,
              with_notes: null,
            })}
          >
            <FormControl.CheckboxFormGroup
              label={`${I18n.t("papp.orders.OrdersListView.with_notes_filter")} (${
                (aggregations && aggregations.with_notes[1]) || 0
              })`}
              checked={filters.with_notes === true}
              indeterminate={filters.with_notes === null}
              onChange={bind(this.handleFilterChange, this, "with_notes")}
            />

            <FormControl.Input
              value={filters.notes}
              onChange={bind(this.handleFilterChange, this, "notes")}
              placeholder={I18n.t("papp.orders.OrdersListView.notes_filter_placeholder")}
              fluid
            />
          </FilterGroup>
        )}

        <FilterGroup
          label={I18n.t("papp.orders.OrdersListView.other_filters")}
          enabled={
            filters.marketplace !== null ||
            filters.saas !== null ||
            filters.manual !== null ||
            filters.automatic !== null ||
            filters.requires_company_approval !== null ||
            filters.doesnt_require_company_approval !== null ||
            filters.no_booking !== null ||
            filters.static_date_booking !== null ||
            filters.static_date_range_booking !== null ||
            filters.without_agent_commision_and_service_fee !== null ||
            filters.with_agent_commision !== null ||
            filters.with_agent_service_fee !== null ||
            filters.with_both_agent_commision_and_service_fee !== null ||
            filters.with_saas_commision !== null
          }
          onClear={bind(this.handleFiltersChange, this, EMPTY_OTHER_FILTERS)}
        >
          <div className="grouped fields">
            <FormControl.CheckboxFormGroup
              label={`${I18n.t("papp.orders.OrdersListView.marketplace_filter")} (${
                (aggregations && aggregations.marketplace[1]) || 0
              })`}
              checked={filters.marketplace !== false}
              onChange={(v) => this.handleFilterChange("marketplace", v ? null : false)}
            />

            <FormControl.CheckboxFormGroup
              label={`${I18n.t("papp.orders.OrdersListView.saas_filter")} (${
                (aggregations && aggregations.saas[1]) || 0
              })`}
              checked={filters.saas !== false}
              onChange={(v) => this.handleFilterChange("saas", v ? null : false)}
            />

            <FormControl.CheckboxFormGroup
              label={`${I18n.t("papp.orders.OrdersListView.manual_filter")} (${
                (aggregations && aggregations.manual[1]) || 0
              })`}
              checked={filters.manual !== false}
              onChange={(v) => this.handleFilterChange("manual", v ? null : false)}
            />

            <FormControl.CheckboxFormGroup
              label={`${I18n.t("papp.orders.OrdersListView.automatic_filter")} (${
                (aggregations && aggregations.automatic[1]) || 0
              })`}
              checked={filters.automatic !== false}
              onChange={(v) => this.handleFilterChange("automatic", v ? null : false)}
            />

            <FormControl.CheckboxFormGroup
              label={`${I18n.t("papp.orders.OrdersListView.requires_company_approval_filter")} (${
                (aggregations && aggregations.requires_company_approval[1]) || 0
              })`}
              checked={filters.requires_company_approval !== false}
              onChange={(v) => this.handleFilterChange("requires_company_approval", v ? null : false)}
            />

            <FormControl.CheckboxFormGroup
              label={`${I18n.t("papp.orders.OrdersListView.doesnt_require_company_approval_filter")} (${
                (aggregations && aggregations.doesnt_require_company_approval[1]) || 0
              })`}
              checked={filters.doesnt_require_company_approval !== false}
              onChange={(v) => this.handleFilterChange("doesnt_require_company_approval", v ? null : false)}
            />

            <FormControl.CheckboxFormGroup
              label={`${I18n.t("papp.orders.OrdersListView.no_booking_filter")} (${
                (aggregations && aggregations.no_booking[1]) || 0
              })`}
              checked={filters.no_booking !== false}
              onChange={(v) => this.handleFilterChange("no_booking", v ? null : false)}
            />

            <FormControl.CheckboxFormGroup
              label={`${I18n.t("papp.orders.OrdersListView.static_date_booking_filter")} (${
                (aggregations && aggregations.static_date_booking[1]) || 0
              })`}
              checked={filters.static_date_booking !== false}
              onChange={(v) => this.handleFilterChange("static_date_booking", v ? null : false)}
            />

            <FormControl.CheckboxFormGroup
              label={`${I18n.t("papp.orders.OrdersListView.static_date_range_booking_filter")} (${
                (aggregations && aggregations.static_date_range_booking[1]) || 0
              })`}
              checked={filters.static_date_range_booking !== false}
              onChange={(v) => this.handleFilterChange("static_date_range_booking", v ? null : false)}
            />

            <FormControl.CheckboxFormGroup
              label={`${I18n.t("papp.orders.OrdersListView.without_agent_commision_and_service_fee_filter")} (${
                (aggregations && aggregations.without_agent_commision_and_service_fee[1]) || 0
              })`}
              checked={filters.without_agent_commision_and_service_fee === true}
              indeterminate={filters.without_agent_commision_and_service_fee === null}
              onChange={bind(this.handleFilterChange, this, "without_agent_commision_and_service_fee")}
            />

            <FormControl.CheckboxFormGroup
              label={`${I18n.t("papp.orders.OrdersListView.with_agent_filter")} (${
                (aggregations && aggregations.with_agent[1]) || 0
              })`}
              checked={filters.with_agent === true}
              indeterminate={filters.with_agent === null}
              onChange={bind(this.handleFilterChange, this, "with_agent")}
            />

            <FormControl.CheckboxFormGroup
              label={`${I18n.t("papp.orders.OrdersListView.with_agent_commision_filter")} (${
                (aggregations && aggregations.with_agent_commision[1]) || 0
              })`}
              checked={filters.with_agent_commision === true}
              indeterminate={filters.with_agent_commision === null}
              onChange={bind(this.handleFilterChange, this, "with_agent_commision")}
            />

            <FormControl.CheckboxFormGroup
              label={`${I18n.t("papp.orders.OrdersListView.with_agent_service_fee_filter")} (${
                (aggregations && aggregations.with_agent_service_fee[1]) || 0
              })`}
              checked={filters.with_agent_service_fee === true}
              indeterminate={filters.with_agent_service_fee === null}
              onChange={bind(this.handleFilterChange, this, "with_agent_service_fee")}
            />

            <FormControl.CheckboxFormGroup
              label={`${I18n.t("papp.orders.OrdersListView.with_both_agent_commision_and_service_fee_filter")} (${
                (aggregations && aggregations.with_both_agent_commision_and_service_fee[1]) || 0
              })`}
              checked={filters.with_both_agent_commision_and_service_fee === true}
              indeterminate={filters.with_both_agent_commision_and_service_fee === null}
              onChange={bind(this.handleFilterChange, this, "with_both_agent_commision_and_service_fee")}
            />

            <FormControl.CheckboxFormGroup
              label={`${I18n.t("papp.orders.OrdersListView.with_saas_commision_filter")} (${
                (aggregations && aggregations.with_saas_commision[1]) || 0
              })`}
              checked={filters.with_saas_commision === true}
              indeterminate={filters.with_saas_commision === null}
              onChange={bind(this.handleFilterChange, this, "with_saas_commision")}
            />
          </div>
        </FilterGroup>
      </Form>
    );
  }
}



// WEBPACK FOOTER //
// ./src/modules/orders/OrdersListView/OrdersListFilters.jsx